import { BehaviorSubject, interval, Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { Injectable, NgZone, OnDestroy, effect, inject, signal } from '@angular/core';

import { ApiService } from '@ng-gems/api';

import { NotificationAdapter } from '@app/core/adapters/notification.adapter';
import { NotificationGetParams } from '@app/core/http-models/notification/notification-get-params.model';
import { NotificationMarkAsReadPatchParams } from '@app/core/http-models/notification/notification-mark-as-read-patch-params.model';
import { WattsonNotification } from '@models/notification/wattson-notification.model';
import { NotificationStatus } from '@models/types/notification-status';

import { AlertService } from './alert.service';
import { AppInfoService } from './app-info.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private api = inject(ApiService);
  private alertService = inject(AlertService);
  private appInfoService = inject(AppInfoService);
  private ngZone = inject(NgZone);
  private notificationsSubject = new BehaviorSubject<WattsonNotification[]>(null);
  private ngUnsubscribe = new Subject();
  private notificationsTrigger = signal<number>(0);
  private notificationAdapter = new NotificationAdapter();
  private interval$: Observable<number> = null;

  constructor() {
    effect(() => {
      if (this.appInfoService.appInfo?.isFeatureOn('useSignalR') && this.notificationsTrigger()) {
        this.fetchNotifications(0);
      }
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  triggerNewNotifications() {
    this.notificationsTrigger.update((value) => value + 1);
  }

  getNotifications(): Observable<WattsonNotification[]> {
    return this.notificationsSubject.asObservable();
  }

  fetchNotifications(delay: number) {
    this.interval$ = this.appInfoService.appInfo.isFeatureOn('useSignalR') ? of(0) : interval(delay);
    this.loadNotifications();
  }

  markAsRead(notification: WattsonNotification) {
    notification.status = NotificationStatus.old;
    this.api.portal.markNotificationAsRead(notification.id, new NotificationMarkAsReadPatchParams(NotificationStatus.old)).subscribe();
    this.notificationsSubject.next(this.notificationsSubject.value);
  }

  /**
   * Get (more) notifications (count is limited by the back)
   * @param lastSeenNotification Last loaded notification into the feed.
   */
  private loadNotifications(lastSeenNotification?: WattsonNotification) {
    const getParams = new NotificationGetParams(
      Boolean(lastSeenNotification) ? lastSeenNotification.id : null,
      Boolean(lastSeenNotification) ? lastSeenNotification.createdAt : null,
    );
    this.ngZone.runOutsideAngular(() => {
      this.interval$
        .pipe(
          startWith(0),
          takeUntil(this.ngUnsubscribe),
          switchMap(() => this.api.portal.getNotifications(getParams)),
          map((it) => this.notificationAdapter.list(it, false)),
        )
        .subscribe({
          next: (loadedNotifications: WattsonNotification[]) => {
            // We don't show alert on new pushed notifications for the first load of them
            if (this.notificationsSubject.value !== null) {
              const ids = this.notificationsSubject.value.map((notification: WattsonNotification) => notification.id);
              const newPushedNotifications = loadedNotifications.filter(
                (notification: WattsonNotification) => !ids.find((id) => id === notification.id),
              );
              if (newPushedNotifications.length) {
                this.ngZone.run(() => {
                  this.processNewPushedNotifications(newPushedNotifications);
                });
              }
            }

            this.notificationsSubject.next(loadedNotifications);
          },
        });
    });
  }

  private processNewPushedNotifications(notifications: WattsonNotification[]) {
    notifications.forEach((notification: WattsonNotification) => {
      if (notification.getAlertMessage()) {
        this.alertService.success(notification.getAlertMessage());
      }
    });
  }
}
